import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { heroFeatures, images } from '../data/landing';
const ease = [0.23, 1, 0.32, 1] as const;
export function Hero() {
  return <header className="pegboard relative w-full overflow-hidden" data-id="element-84" data-mp="4lyxkshw2qgt">
      <div className="mx-auto flex max-w-5xl flex-col items-center px-5 pb-16 pt-12 text-center md:pb-20 md:pt-16" data-id="element-85" data-mp="4jm53lei8xpi">
        <motion.div className="flex flex-col items-center" initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-86" data-mp="xae9351taqne">
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 font-display text-sm font-semibold text-teal shadow-soft md:text-base" data-id="element-87" data-mp="ncglmjrs0xvu">
            <SparklesIcon className="h-4 w-4" aria-hidden data-id="element-88" data-mp="3d2tpxe128sgn" />
            For curious minds aged 4 to 12
          </span>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl md:text-6xl" data-id="element-89" data-mp="384s5aonzkl32">
            Turn your home into a creative STEM lab and get your kids{' '}
            <span className="relative whitespace-nowrap text-teal" data-id="element-90" data-mp="39alawhhz8u4q">
              off screens
              <svg className="absolute -bottom-2 left-0 h-3 w-full text-sun" viewBox="0 0 200 12" preserveAspectRatio="none" aria-hidden data-id="element-91" data-mp="3e9fqlakne2km">
                <path d="M2 9 C 50 2, 150 2, 198 8" stroke="currentColor" strokeWidth="5" fill="none" strokeLinecap="round" data-id="element-92" data-mp="30gzuktq5z4t7" />
              </svg>
            </span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-inkSoft md:text-xl" data-id="element-93" data-mp="2qkdboaheentt">
            Discover <strong className="text-ink" data-id="element-94" data-mp="2viewazuh2v7e">Maker Lab™</strong>: the ultimate STEM project library for curious kids. Over 180 amazing
            experiments and robotics projects using simple everyday items from around the house.
          </p>

          <CTAButton className="mt-8" href="#offer-summary" data-id="element-95" data-mp="2lk8wcns87z56">Yes, I want instant access!</CTAButton>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-inkSoft" data-id="element-96" data-mp="s3mb9x0ezovq">
            <ShieldCheckIcon className="h-4 w-4 text-teal" aria-hidden data-id="element-97" data-mp="n9qkpfmhd4al" />
            Protected purchase · instant digital delivery
          </span>

          <div className="mt-7 flex items-center gap-3" data-id="element-98" data-mp="ddf4iejrruav">
            <div className="flex -space-x-2" aria-hidden data-id="element-99" data-mp="deyitzlv7va0">
              {['bg-berry', 'bg-teal', 'bg-tang', 'bg-sky'].map((c, i) => <span key={c} className={`flex h-9 w-9 items-center justify-center rounded-full border-2 border-white font-display text-sm font-bold ${i === 3 ? 'text-teal' : 'text-white'} ${c}`} data-id="element-100" data-mp="1622idp26vhee" data-mpi={i}>
                  {['J', 'M', 'D', '+'][i]}
                </span>)}
            </div>
            <div className="text-left" data-id="element-101" data-mp="3lsp1x9cpsbcb">
              <div className="flex items-center gap-0.5" aria-label="Rated 5 out of 5" data-id="element-102" data-mp="3ltqeji9dp5w5">
                {Array.from({
                length: 5
              }).map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-sun text-sun" aria-hidden data-id="element-103" data-mp="1x9b590wn91ck" data-mpi={i} />)}
                <span className="ml-2 text-sm font-extrabold text-teal" data-id="element-104" data-mp="21amx7d2zpynx">+250 today</span>
              </div>
              <p className="text-sm font-semibold text-inkSoft" data-id="element-105" data-mp="269hda79kp94c">1,250+ families transformed their afternoons</p>
            </div>
          </div>
        </motion.div>

        <motion.div className="relative mt-14 w-full max-w-2xl" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease,
        delay: 0.08
      }} data-id="element-106" data-mp="2b2so0jhiaa1j">
          <div className="overflow-hidden rounded-[36px] bg-white p-2 shadow-lift" data-id="element-107" data-mp="2ozz589ml00on">
            <img src={images.hero} alt="Two smiling kids building a robot car and a wind turbine with Maker Lab" className="aspect-square w-full rounded-[30px] object-cover" data-id="element-108" data-mp="2tzbpcmm2itnj" />
          </div>
          <div className="animate-bob absolute -left-3 top-16 rounded-2xl bg-white px-4 py-3 text-left shadow-lift md:-left-14" data-id="element-109" data-mp="zlkj7x2y6rwu">
            <p className="font-display text-2xl font-bold leading-none text-berry" data-id="element-110" data-mp="4aky925e4qhk">180+</p>
            <p className="text-xs font-extrabold uppercase text-inkSoft" data-id="element-111" data-mp="3vi6yekswiato">Projects</p>
          </div>
          <div className="animate-bob absolute -right-3 bottom-20 rounded-2xl bg-white px-4 py-3 text-left shadow-lift [animation-delay:1.2s] md:-right-14" data-id="element-112" data-mp="ea3gne5nk47i">
            <p className="font-display text-lg font-bold leading-none text-teal" data-id="element-113" data-mp="98e23oj818eo">Ages 4–12</p>
            <p className="text-xs font-bold text-inkSoft" data-id="element-114" data-mp="3ho672c4a3q3c">Solo or together</p>
          </div>
        </motion.div>

        <ul className="mt-14 flex flex-wrap items-center justify-center gap-3" data-id="element-115" data-mp="3cpct6il2xx2n">
          {heroFeatures.map(({
          icon: Icon,
          label
        }) => <li key={label} className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 font-display text-base font-semibold text-ink shadow-soft" data-id="element-116" data-mp="3mp3lcdm63zzk">
              <Icon className="h-5 w-5 text-teal" aria-hidden data-id="element-117" data-mp="2yxg4koime5b0" />
              {label}
            </li>)}
        </ul>
      </div>
    </header>;
}